import { Component, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

/** Opções de intervalo de atualização em milissegundos */
const REFRESH_INTERVALS = {
  OFF: 0,
  THIRTY_SECONDS: 30_000,
  ONE_MINUTE: 60_000,
  NINETY_SECONDS: 90_000
} as const;

const STORAGE_KEY = 'autoRefreshInterval';

const VALID_INTERVALS = new Set<number>(Object.values(REFRESH_INTERVALS));

function loadStoredInterval(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === null) {
    return REFRESH_INTERVALS.OFF;
  }

  const parsed = Number(stored);
  return VALID_INTERVALS.has(parsed) ? parsed : REFRESH_INTERVALS.OFF;
}

@Component({
  selector: 'app-auto-refresh-selector',
  templateUrl: './auto-refresh-selector.component.html',
  styleUrls: ['./auto-refresh-selector.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgbDropdownModule]
})
export class AutoRefreshSelectorComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly refresh = output<void>();

  readonly refreshControl = new FormControl<number>(REFRESH_INTERVALS.OFF);

  readonly intervals = REFRESH_INTERVALS;

  readonly countdownSeconds = signal(0);

  setInterval(value: number): void {
    this.refreshControl.setValue(value);
  }

  get selectedLabel(): string {
    const value = this.refreshControl.value;
    switch (value) {
      case REFRESH_INTERVALS.THIRTY_SECONDS:
        return '30 seg';
      case REFRESH_INTERVALS.ONE_MINUTE:
        return '1 min';
      case REFRESH_INTERVALS.NINETY_SECONDS:
        return '1m 30s';
      default:
        return 'Não atualizar';
    }
  }

  ngOnInit(): void {
    this.setupRefreshTimer();
    this.setupCountdownTimer();
    this.setupPersistence();
    this.refreshControl.setValue(loadStoredInterval(), { emitEvent: true });
  }

  private setupPersistence(): void {
    this.refreshControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value !== null && value !== undefined) {
          localStorage.setItem(STORAGE_KEY, String(value));
        }
      });
  }

  private setupRefreshTimer(): void {
    this.refreshControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(intervalMs => {
          if (!intervalMs || intervalMs <= 0) {
            return EMPTY;
          }
          // Delay inicial igual ao intervalo para evitar refresh imediato,
          // garantindo que o timer reinicie quando o usuário muda a seleção.
          return timer(intervalMs, intervalMs);
        })
      )
      .subscribe(() => {
        this.refresh.emit();
      });
  }

  private setupCountdownTimer(): void {
    this.refreshControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(intervalMs => {
          if (!intervalMs || intervalMs <= 0) {
            this.countdownSeconds.set(0);
            return EMPTY;
          }

          const totalSeconds = Math.max(1, Math.floor(intervalMs / 1000));
          this.countdownSeconds.set(totalSeconds);

          return timer(1000, 1000).pipe(
            map(tick => {
              const remaining = totalSeconds - ((tick + 1) % totalSeconds);
              return remaining === 0 ? totalSeconds : remaining;
            })
          );
        })
      )
      .subscribe(remaining => {
        this.countdownSeconds.set(remaining);
      });
  }
}
